import { Icon } from '@mui/material';
import { CreateFacilityItem, Facility, FacilityType } from '@schooly/api';
import { getChildrenByParentId } from '@schooly/hooks/use-facilities';
import { ArrowAngleIcon, ArrowDownV2Icon } from '@schooly/style';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { FacilityAction } from './FacilityCard';
import { FacilityField, FacilityFormItem } from './SchoolGeneralFacilitiesModalContent';

export const facilityToFormItem = (f: Facility): FacilityFormItem => ({
  id: f.id,
  name: f.name,
  type: f.type,
  parent_id: f.parent_id,
  used_in_timetables: f.used_in_timetables,
});

export const formItemToCreateFacilityItem = (item: FacilityFormItem): CreateFacilityItem => ({
  id: item.id,
  name: item.name,
  type: item.type,
  parent_id: item.parent_id,
});

export const createEmptyFacility = (
  type: FacilityType,
  parentId: string | null = null,
): FacilityFormItem => ({
  id: uuidv4(),
  name: '',
  type,
  parent_id: parentId,
  used_in_timetables: false,
});

const canAttachFacility = (
  facility: FacilityField,
  counts: { campus: number; building: number },
) => {
  const parentMinCount = facility.parent_id ? 1 : 0;

  switch (facility.type) {
    case FacilityType.Building:
      return counts.campus > parentMinCount;
    case FacilityType.Room:
      return counts.building > parentMinCount;
    default:
      return false;
  }
};

type DefaultLabels = Record<FacilityType, string>;

type BuildRoomAttachSubActionsParams = {
  campuses: FacilityField[];
  standaloneBuildings: FacilityField[];
  childrenByParentId: Record<string, FacilityField[]>;
  defaultLabels: DefaultLabels;
  selectedParentId: string | null;
  onAttach: (parentId: string) => void;
};

const buildRoomAttachSubActions = ({
  campuses,
  standaloneBuildings,
  childrenByParentId,
  defaultLabels,
  selectedParentId,
  onAttach,
}: BuildRoomAttachSubActionsParams): FacilityAction[] => {
  const actions: FacilityAction[] = [];

  for (const campus of campuses) {
    const campusBuildings = childrenByParentId[campus.id] ?? [];
    if (campusBuildings.length === 0) continue;

    actions.push({ label: campus.name.trim() || defaultLabels.campus });

    for (const building of campusBuildings) {
      actions.push({
        label: building.name.trim() || defaultLabels.building,
        isSelected: building.id === selectedParentId,
        startIcon: (
          <Icon sx={{ color: 'common.grey' }}>
            <ArrowAngleIcon />
          </Icon>
        ),
        onClick: () => onAttach(building.id),
      });
    }
  }

  for (const building of standaloneBuildings) {
    actions.push({
      label: building.name.trim() || defaultLabels.building,
      isSelected: building.id === selectedParentId,
      onClick: () => onAttach(building.id),
    });
  }

  return actions;
};

type BuildBuildingAttachSubActionsParams = {
  campuses: FacilityField[];
  defaultLabels: DefaultLabels;
  selectedParentId: string | null;
  onAttach: (parentId: string) => void;
};

const buildBuildingAttachSubActions = ({
  campuses,
  defaultLabels,
  selectedParentId,
  onAttach,
}: BuildBuildingAttachSubActionsParams): FacilityAction[] =>
  campuses.map((c) => ({
    label: c.name.trim() || defaultLabels.campus,
    isSelected: c.id === selectedParentId,
    onClick: () => onAttach(c.id),
  }));

type UseGetFacilityMenuActionsParams = {
  campuses: FacilityField[];
  buildingCount: number;
  standaloneBuildings: FacilityField[];
  childrenByParentId: Record<string, FacilityField[]>;
  handleAttach: (index: number, parentId: string) => void;
  handleDetach: (index: number) => void;
};

export const useGetFacilityMenuActions = ({
  campuses,
  buildingCount,
  standaloneBuildings,
  childrenByParentId,
  handleAttach,
  handleDetach,
}: UseGetFacilityMenuActionsParams): ((facility: FacilityField) => FacilityAction[]) => {
  const { formatMessage } = useIntl();
  const campusCount = campuses.length;

  const defaultLabels: DefaultLabels = useMemo(
    () => ({
      campus: formatMessage({ id: 'school-tabs-Facilities-Campus' }),
      building: formatMessage({ id: 'school-tabs-Facilities-Building' }),
      room: formatMessage({ id: 'school-tabs-Facilities-Room' }),
    }),
    [formatMessage],
  );

  const getMenuActions = useCallback(
    (facility: FacilityField): FacilityAction[] => {
      const actions: FacilityAction[] = [];

      const canDetach = facility.parent_id !== null;
      const canAttach = canAttachFacility(facility, {
        campus: campusCount,
        building: buildingCount,
      });

      if (canAttach) {
        const onAttach = (parentId: string) => handleAttach(facility.index, parentId);

        const subActions =
          facility.type === FacilityType.Room
            ? buildRoomAttachSubActions({
                campuses,
                standaloneBuildings,
                childrenByParentId,
                defaultLabels,
                selectedParentId: facility.parent_id,
                onAttach,
              })
            : buildBuildingAttachSubActions({
                campuses,
                defaultLabels,
                selectedParentId: facility.parent_id,
                onAttach,
              });

        actions.push({
          label: formatMessage({ id: 'school-tabs-Facilities-AttachTo' }),
          endIcon: (
            <Icon sx={(theme) => ({ width: theme.spacing(1), transform: 'rotate(-90deg)' })}>
              <ArrowDownV2Icon />
            </Icon>
          ),
          subActions,
        });
      }

      if (canDetach) {
        actions.push({
          label: formatMessage({ id: 'school-tabs-Facilities-Detach' }),
          onClick: () => handleDetach(facility.index),
        });
      }

      return actions;
    },
    [
      campusCount,
      buildingCount,
      campuses,
      standaloneBuildings,
      childrenByParentId,
      defaultLabels,
      formatMessage,
      handleAttach,
      handleDetach,
    ],
  );

  return getMenuActions;
};

export const validateFacilityName = (
  facilities: CreateFacilityItem[],
  index: number,
): string | true => {
  const current = facilities[index];
  if (!current) return true;

  const name = current.name.trim();
  if (!name) return true;

  const hasDuplicate = facilities.some(
    (f, i) =>
      i !== index &&
      f.type === current.type &&
      f.parent_id === current.parent_id &&
      f.name.trim().toLowerCase() === name.toLowerCase(),
  );

  if (!hasDuplicate) return true;

  return current.parent_id
    ? `school-tabs-Facilities-NameDuplicatedInGroup-${current.type}`
    : `school-tabs-Facilities-NameDuplicated-${current.type}`;
};

export const getAllDescendants = <T extends Pick<Facility, 'id' | 'type' | 'parent_id'>>(
  childrenByParentId: Record<string, T[]>,
  parentId: string,
): T[] => {
  const result: T[] = [];

  const collect = (id: string) => {
    const children = getChildrenByParentId(childrenByParentId, id);
    for (const child of children) {
      result.push(child);
      collect(child.id);
    }
  };

  collect(parentId);
  return result;
};

type GetDeleteConfirmationParams = {
  type: FacilityType;
  usedInTimetables: boolean;
  isExisting: boolean;
  hasChildren: boolean;
};

export const getDeleteConfirmationTextId = ({
  type,
  usedInTimetables,
  isExisting,
  hasChildren,
}: GetDeleteConfirmationParams): string | null => {
  const base = `school-tabs-Facilities-DeleteConfirmation-${type}`;

  switch (type) {
    case FacilityType.Room:
      if (!isExisting) return null;
      return usedInTimetables ? `${base}-UsedInTimetables` : base;

    case FacilityType.Campus:
    case FacilityType.Building:
      if (!isExisting && !hasChildren) return null;
      if (usedInTimetables) return `${base}-UsedInTimetables`;
      if (hasChildren) return `${base}-WithChildren`;
      return base;
  }
};
